import { useCallback, useState } from 'react';
import { authAxios } from '@/lib/axios';

interface UploadUrlResponse {
  url: string;
  cdnUrl: string | null;
}

interface UseUploadJsonReturn {
  uploadJson: (data: unknown) => Promise<string | null>;
  isLoading: boolean;
  error: string | null;
}

export function useUploadJson(): UseUploadJsonReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadJson = useCallback(async (data: unknown): Promise<string | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const contentType = 'application/json';
      const response = await authAxios.post<UploadUrlResponse>('/api/get-upload-url', {
        contentType,
      });

      const { cdnUrl } = response.data;
      const parsedURL = new URL(response.data.url);
      const body = JSON.stringify(data);

      const putResponse = await fetch(response.data.url, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body,
      });

      if (!putResponse.ok) {
        throw new Error(`Upload failed with status ${putResponse.status}`);
      }

      return cdnUrl || `${parsedURL.protocol}//${parsedURL.host}${parsedURL.pathname}`;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to upload JSON';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { uploadJson, isLoading, error };
}
